import { useCallback, useEffect, useState } from "react";
import * as turf from "@turf/turf";
import "maplibre-gl/dist/maplibre-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { anyType } from "../types";
import { hiddenMeasureLabel, showMeasureLabel } from "../lib/measureControl";
import { geojson, INITIAL_DISTANCE_LABEL } from "../constants/components/measureDistanceControl";
import type { Feature, Point, Polygon } from 'geojson';
import { getLayersBySourceId, updateResultLabelSource } from '../lib/mapUtils';
import { DISTANCE_POINT_LAYER } from "./useMeasureDistanceControl";



export const AREA_DISTANCE_SOURCE = 'area-measurement';

export const useMeasureAreaControl = (map: maplibregl.Map | null, isOpen: boolean) => {

  const [distanceLabel, setDistanceLabel] = useState(INITIAL_DISTANCE_LABEL);


  const onClickMapHandler = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    if (!map) { return; }
  
    // クリック位置にあるマーカー（既存のポイント）を取得
    const features = map.queryRenderedFeatures(e.point, {
      layers: [DISTANCE_POINT_LAYER]
    });
  
    // マーカーをクリックした場合は、そのマーカーを削除
    if (features.length) {
      const id = features[0].properties.id;
      geojson.features = geojson.features.filter((point: Feature) => {
        return point.properties && point.properties.id !== id;
      });
    } else {
      // クリック位置に新たなポイントを追加
      const point: Feature<Point> = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [e.lngLat.lng, e.lngLat.lat]
        },
        properties: {
          id: String(new Date().getTime())
        }
      };
      geojson.features.push(point);
    }
  
    // 面積計測：ポイントが3点以上あればポリゴンを生成して面積を計算
    if (geojson.features.length > 2) {
      // 各ポイントの座標を取得
      const coordinates = geojson.features.filter((f: Feature) => f.geometry.type === 'Point').map((point: anyType) => {
        return (point.geometry as Point).coordinates;
      });
  
      // ポリゴンを作成するために、最初の座標を最後に追加（閉じたリングにする）
      if (
        coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
        coordinates[0][1] !== coordinates[coordinates.length - 1][1]
      ) {
        coordinates.push(coordinates[0]);
      }
  
      // 以前に生成されていたポリゴンがある場合は除外してから、ポリゴンを追加
      geojson.features = geojson.features.filter((f: Feature) => f.geometry.type !== 'Polygon');
      const polygon: Feature<Polygon> = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        },
        properties: {}
      };
      geojson.features.push(polygon);
  
      // 面積を計算（単位は平方メートル）
      const area = turf.area(polygon);
      let areaLabel: string;
  
      // 1,000,000平方メートル未満ならm²、以上ならkm²で表示
      if (area < 1000000) {
        areaLabel = `面積: ${area.toLocaleString('ja-JP', { maximumFractionDigits: 2 })}m²`;
      } else {
        areaLabel = `面積: ${(area / 1000000).toLocaleString('ja-JP', { maximumFractionDigits: 2 })}km²`;
      }
      setDistanceLabel(areaLabel); // 必要に応じ変数名をsetAreaLabelなどに変更してください
  
      // ポリゴンの重心を取得してラベルを表示
      const centroid = turf.centroid(polygon).geometry.coordinates;
      showMeasureLabel(map, areaLabel, centroid);
    }
  
    // ソースを更新して地図上に反映
    updateResultLabelSource(map, AREA_DISTANCE_SOURCE, geojson);
  }, [map]);


  useEffect(() => {
    if (!map) { return; }
  
    if(isOpen) {
      // 描画のためのソースとレイヤーを表示
      getLayersBySourceId(map, AREA_DISTANCE_SOURCE).forEach((layerId) => {
        map.setLayoutProperty(layerId, 'visibility', 'visible');
      });
      map.on('click', onClickMapHandler);
      
    } else {
      map.off('click', onClickMapHandler);

      // 描画のためのソースとレイヤーを非表示
      geojson.features = [];
      updateResultLabelSource(map, AREA_DISTANCE_SOURCE, geojson);

      // 計測結果ラベルを非表示
      hiddenMeasureLabel(map);

      setDistanceLabel(INITIAL_DISTANCE_LABEL);
    }

    return () => {
      map.off('click', onClickMapHandler);
    }

  }, [isOpen, map, onClickMapHandler]);


  // 距離ラベルを返す
  return isOpen ? distanceLabel : undefined;
};
